import axios from 'axios';
import { pipe } from 'fp-ts/function';
import * as TE from 'fp-ts/TaskEither';
import { formatValidationErrors } from 'io-ts-reporters';
import * as CoreStore from '../../store/core';
import * as ConfigStore from '../../store/initialConfiguration';
import * as Hooks from 'preact/hooks';
import { useDispatch } from 'react-redux';
import * as EC from '@fixtuur/error-context';
import * as Selectors from '../../store/selectors';
import { useSelector } from './redux';
import {
    InitialConfigurationDocumentT,
    InitialConfigurationDocument,
} from '../../data/shareConfiguration';
import { AppError } from '../../data/appError';
import * as Logging from '../../store/actions/logging';
import { PropertyValue } from '../configuration/types';

function fetchInitialConfigurationDocument(
    layoutUrl: string,
    configurationId: string,
    tenant: string,
): TE.TaskEither<EC.ErrorContext<string>, InitialConfigurationDocumentT> {
    return pipe(
        TE.Do,
        TE.bind('response', () =>
            TE.tryCatch(
                () =>
                    axios.get(`${layoutUrl}/api/v1/${tenant}/configurations/${configurationId}`, {
                        // 40s
                        timeout: 40 * 1000,
                    }),
                (reason: unknown) => EC.fromUnknown<string>(reason),
            ),
        ),
        TE.bind('layout', ({ response }) =>
            response.status === 200
                ? TE.right<EC.ErrorContext<string>, unknown>(response.data)
                : TE.left<EC.ErrorContext<string>, unknown>(
                      EC.construct('failed to fetch layout, expected response 200', {
                          responseStatus: response.status.toString(),
                          responseStatusText: response.statusText,
                          response: JSON.stringify(response, null, 2),
                      }),
                  ),
        ),
        TE.bind('decodedConfigurationDocument', ({ layout }) =>
            pipe(
                TE.fromEither(InitialConfigurationDocument.decode(layout)),
                TE.mapLeft(formatValidationErrors),
                TE.mapLeft(errors =>
                    EC.construct('failed to decode layout', {
                        layout: JSON.stringify(layout, null, 2),
                        errors: JSON.stringify(errors, null, 2),
                    }),
                ),
            ),
        ),
        TE.map(({ decodedConfigurationDocument }) => decodedConfigurationDocument),
    ) as TE.TaskEither<EC.ErrorContext<string>, InitialConfigurationDocumentT>;
}

function isSaveData(
    launchData:
        | { configurationId: string }
        | { configuratorId: string; startWithSelections: Record<string, PropertyValue> },
): launchData is { configurationId: string } {
    return (launchData as { configurationId: string }).configurationId !== undefined;
}

/**
 */
export const useLoadConfiguration = async () => {
    const layoutUrl = useSelector(Selectors.Core.layoutUrl);
    const gavelUrl = useSelector(Selectors.Core.configurationServiceUrl);
    const tenantIdentifier = useSelector(Selectors.Core.tenantIdentifier);

    const launchData = useSelector(Selectors.Core.configurationLaunchData);

    const dispatch = useDispatch();

    Hooks.useEffect(() => {
        if (launchData && !isSaveData(launchData) && tenantIdentifier) {
            dispatch(
                ConfigStore.setInitialConfiguration({
                    configuratorId: launchData.configuratorId,
                    startingSelections: launchData.startWithSelections,
                    startingAttachments: {},
                }),
            );
        } else if (launchData && isSaveData(launchData) && tenantIdentifier) {
            const fetchAndSet = async () => {
                if (layoutUrl !== undefined) {
                    pipe(
                        fetchInitialConfigurationDocument(
                            layoutUrl,
                            launchData.configurationId,
                            tenantIdentifier,
                        ),
                        TE.mapLeft(
                            EC.extendContext({
                                requestedConfigurationId: launchData.configurationId,
                            }),
                        ),
                        TE.mapLeft(ec => {
                            dispatch(CoreStore.error(AppError.CONFIGURATION_LOAD_FAILED));
                            dispatch(Logging.logError(ec.message, ec.context));
                        }),
                        TE.map(configuration =>
                            dispatch(
                                ConfigStore.setInitialConfiguration({
                                    configuratorId: configuration.configuratorId,
                                    startingSelections: configuration.selections.properties,
                                    startingAttachments: configuration.selections.attachments,
                                }),
                            ),
                        ),
                    )();
                }
            };
            fetchAndSet();
        }
    }, [layoutUrl, launchData, tenantIdentifier, gavelUrl]);
};
